import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import SignaturePad from "react-signature-canvas";
import WebService from "../utility/WebService";
import { toast } from "react-toastify";
import { Border } from "react-bootstrap-icons";

interface propData {
  data: any;
  companyCode: number;
  techNum: string;
  optionNum?: string | number;
  onClose?: (status?: boolean) => void;
}

const QuoteApproval = ({ data,companyCode,techNum, optionNum, onClose }: propData) => {
  const [showTerms, setShowTerms] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signatureBy, setSignatureBy] = useState("");
  const [TandC, setTandC] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setLoading] = useState(false);

  const sigPadRef = useRef<SignaturePad>(null);

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const validateEmail = (email: string) =>
    email === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailTo(value);

    if (!validateEmail(value)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  // Load initial values
  useEffect(() => {
    if (!data) return;

    setTandC(data?.termsAndConditionDescription || "");
    setSignatureBy(data?.contactName || "");
    setEmailTo(data?.smEmail || "");
  }, [data]);

  const onSubmit = () => {
    if (!accepted) {
      alert("You must accept the terms and conditions before submitting.");
      return;
    }

    if (sigPadRef.current?.isEmpty()) {
      alert("Please provide a signature first!");
      return;
    }

    if (!signatureBy || !emailTo) {
      alert("Please fill all the fields.");
      return;
    }

    const signatureData = sigPadRef.current!.toDataURL();
    const base64 = signatureData?.replace("data:image/png;base64,", "");
    const binary = atob(base64);
    const byteArray = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      byteArray[i] = binary.charCodeAt(i);
    }
    const byteList = Array.from(byteArray);
    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + localStorage.getItem("token"),
    };

    setLoading(true);
    const payload = {
        companyCode: companyCode,
        quoteNum: data?.quoteNum,
        signedBy: signatureBy,
        signature: base64,
        signatureBytes: base64,
        ...(optionNum !== undefined && { optionNum })
      };

    //console.log("FINAL PAYLOAD SENT:", payload);
    axios
      .post(
        `${WebService.getUrl()}api/quote/saveQuoteSignature`,
         payload,
        { headers }
      )
      .then((res: any) => {
         if (!res.data?.success) {
            toast.error(res.errorInfo?.Message)
            setLoading(false);
          }
          else
          {  
            toast.success("Quote Approved Successfully");
            sendEmail(res.data?.success);
          }
       // setLoading(false);
       // onClose?.();
      })
      .catch(() => {
        toast.error("Error approving quote");
        setLoading(false);
      });
  };

   const sendEmail = (status: boolean) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + localStorage.getItem("token"),
    };

    setLoading(true);
    const payload = {
        companyCode: companyCode,
        quoteNum: data?.quoteNum,
        serviceMaster: data?.servMasterNum,
        techNum: techNum,
        toEmails: emailTo ? [emailTo] : [],
    bccEmails: data?.fromEmail ? [data?.fromEmail] : [],
        comments: '--QuoteApproval--' 
      };

    //console.log("FINAL PAYLOAD SENT:", payload);
    axios
      .post(
        `${WebService.getUrl()}api/quote/email`,
         payload,
        { headers }
      )
      .then(() => {
        toast.success("Email sent Successfully");
        setLoading(false);
        onClose?.(true);
      })
      .catch(() => {
        toast.error("Error sending email.");
        setLoading(false);
      });
  };
  
  return (

    <div style={{ padding: 5 }}>
      <div>
        <Button
          variant="link"
          onClick={() => setShowTerms(true)}
          style={{ padding: 0 }}
        >
          View Terms & Conditions
        </Button>
      </div>

      <Form.Check
        type="checkbox"
        label="I have reviewed and accept the terms and conditions"
        checked={accepted}
        onChange={(e) => setAccepted(e.target.checked)}
        className="mb-3"
      />

      <Form.Group className="mb-3">
        <Form.Label>Signed By</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter your name"
          value={signatureBy}
          onChange={(e) => setSignatureBy(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Signature</Form.Label>

        <div
          style={{
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "5px",
            width: "100%",
            maxWidth: "450px",
            background: "#fafafa",
          }}
        >
          <SignaturePad
            ref={sigPadRef}
            canvasProps={{
              width: 420,
              height: 150,
              className: "sigCanvas",
              style: { backgroundColor: "white" },
            }}
          />

          <div className="text-end mt-2">
            <Button variant="secondary" size="sm" onClick={clearSignature}>
              Clear
            </Button>
          </div>
        </div>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Email</Form.Label>
        <Form.Control
          type="email"
          placeholder="Enter email"
          value={emailTo}
          onChange={handleEmailChange}
          isInvalid={!!emailError}
        />
        <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>
      </Form.Group>

      <Button style={{
            border: 0,
            background: "#F55943",
            color: "white",
            borderRadius: "5px",
            display: "inline-block",
            cursor: "pointer"
          }}
        variant="primary"
        onClick={onSubmit}
        className="w-100"
        disabled={isLoading}
      >
        {isLoading ? "Submitting..." : "Submit"}
      </Button>

      <Modal show={showTerms} onHide={() => setShowTerms(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Terms & Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <div dangerouslySetInnerHTML={{ __html: TandC }} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTerms(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuoteApproval;
